import { NavLink } from "react-router-dom";
import { X, PanelLeft, PanelLeftClose, Check, ChevronRight } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DEFAULT_SIDEBAR_ICON } from "@/lib/navRegistry";
import { cn } from "@/lib/utils";

const MODES = [
  { v: "expanded", l: "Expanded" },
  { v: "compact", l: "Compact" },
  { v: "hidden", l: "Hidden" },
];

function ModeMenu({ mode, onModeChange, compact }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="sidebar-mode-trigger"
          aria-label="Sidebar display mode"
          title="Sidebar display"
          className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-fg/60 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-active">
          {compact ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuLabel className="text-xs">Sidebar display</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {MODES.map((m) => (
          <DropdownMenuItem key={m.v} onClick={() => onModeChange(m.v)} data-testid={`sidebar-mode-${m.v}`}>
            <span className="flex-1">{m.l}</span>
            {mode === m.v && <Check className="h-4 w-4 text-accent" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Item({ it, compact, onNavigate }) {
  const Icon = it.icon || DEFAULT_SIDEBAR_ICON;
  return (
    <NavLink
      key={it.id} to={it.route} end onClick={onNavigate} data-testid={`side-${it.id}`}
      title={compact ? it.label : undefined} aria-label={it.label}
      className={({ isActive }) => cn(
        "group flex items-center rounded-lg text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-active",
        compact ? "justify-center px-0 py-2.5" : "gap-2.5 px-3 py-2",
        isActive ? "bg-sidebar-accent text-sidebar-fg" : "text-sidebar-fg/80 hover:bg-sidebar-accent/50 hover:text-sidebar-fg")}>
      {({ isActive }) => (
        compact ? (
          <span className="relative flex h-8 w-8 items-center justify-center">
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
            {isActive && <span className="absolute -left-2 h-4 w-1 rounded-full bg-sidebar-active" />}
          </span>
        ) : (
          <>
            <span className={cn("h-4 w-1 shrink-0 rounded-full", isActive ? "bg-sidebar-active" : "bg-transparent")} />
            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
            <span className="truncate">{it.label}</span>
            {!it.ready && <span className="ml-auto rounded bg-sidebar-accent/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-sidebar-active/80">Soon</span>}
          </>
        )
      )}
    </NavLink>
  );
}

function Panel({ title, items, mode = "expanded", onModeChange, onClose }) {
  const compact = mode === "compact";
  return (
    <div className="flex h-full flex-col bg-sidebar bg-gradient-sidebar">
      <div className={cn("flex h-16 items-center border-b border-sidebar-border", compact ? "justify-center px-2" : "justify-between px-4")}>
        {!compact && <span className="truncate font-heading text-lg font-semibold text-sidebar-fg">{title}</span>}
        <div className="flex items-center gap-1">
          {onModeChange && <ModeMenu mode={mode} onModeChange={onModeChange} compact={compact} />}
          {onClose && <button onClick={onClose} className="text-sidebar-fg/70 lg:hidden" data-testid="sidebar-close"><X className="h-5 w-5" /></button>}
        </div>
      </div>
      <nav className={cn("sidebar-scroll flex-1 space-y-0.5 overflow-y-auto py-4", compact ? "px-2" : "px-3")}>
        {items.map((it) => <Item key={it.id} it={it} compact={compact} onNavigate={onClose} />)}
      </nav>
    </div>
  );
}

export function ContextualSidebar({ title, items, mode = "expanded", onModeChange, mobileOpen, onClose }) {
  const hasItems = !!items?.length;

  // Desktop: hidden mode shows only a slim restore rail.
  if (hasItems && mode === "hidden") {
    return (
      <aside className="hidden w-9 shrink-0 border-r border-sidebar-border bg-sidebar bg-gradient-sidebar lg:flex lg:flex-col lg:items-center lg:pt-4" data-testid="sidebar-restore-rail">
        <button
          onClick={() => onModeChange?.("expanded")}
          data-testid="sidebar-restore"
          aria-label="Show sidebar" title="Show sidebar"
          className="flex h-9 w-7 items-center justify-center rounded-md text-sidebar-fg/60 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-active">
          <ChevronRight className="h-4 w-4" />
        </button>
      </aside>
    );
  }

  return (
    <>
      {hasItems && (
        <aside className="hidden shrink-0 lg:block"
          style={{ width: mode === "compact" ? "var(--sidebar-compact-w, 64px)" : "var(--sidebar-w, 248px)" }}
          data-testid="contextual-sidebar" data-mode={mode}>
          <Panel title={title} items={items} mode={mode} onModeChange={onModeChange} />
        </aside>
      )}
      {!hasItems && <aside className="hidden lg:block" />}

      {/* Mobile / tablet drawer — always full labels for touch */}
      {mobileOpen && hasItems && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="sidebar-overlay" />
          <div className="absolute left-0 top-0 h-full w-[280px] animate-fade-up shadow-lift">
            <Panel title={title} items={items} mode="expanded" onClose={onClose} />
          </div>
        </div>
      )}
    </>
  );
}
